import { NotificationLoadMoreClientsVariables, PageInfoFields } from '@ms/yammer-graphql';
import queryNotificationLoadMore from '@ms/yammer-graphql/dist/operations/queryNotificationLoadMore.graphql';
import queryNotificationUnseenCount from '@ms/yammer-graphql/dist/operations/queryNotificationUnseenCount.graphql';
import { RequestSkippedReason } from '@ms/yammer-telemetry';

import { GraphqlApiRequest, ManagedApiLifecycle, getGraphqlApiRequest, managedApiAction } from '../..';
import { RetryOptions, retryableManagedApiAction } from '../../api/retryableManagedApiAction';
import { normalizeNotificationsLoadMore } from '../../normalization/graphql/normalizeNotifications';
import { getRetryDelay } from '../../realtime';
import { YammerState, notificationUnseenCountRequestId } from '../../state/types';
import { reportError, reportInfo } from '../../telemetry';
import { PromiseThunkAction } from '../../types';
import { getExperimentTreatment } from '../experiments/selectors';
import { getFulfilledTime, isPending } from '../requestStatuses/selectors';

import {
  NotificationLoadMoreFulfilledAction,
  NotificationLoadMorePendingAction,
  NotificationLoadMoreRejectedAction,
  NotificationLoadMoreResponse,
  NotificationUnseenCountLoadFulfilledAction,
  NotificationUnseenCountLoadPendingAction,
  NotificationUnseenCountLoadRejectedAction,
  NotificationUnseenCountResponse,
} from './actions.types';
import { notificationPageSize } from './selectors';

const loadNotificationUnseenCountPending = (): NotificationUnseenCountLoadPendingAction => ({
  type: 'NOTIFICATION_UNSEEN_COUNT.LOAD_PENDING',
});

export const loadNotificationUnseenCountFulfilled = (
  unseenCount: number
): NotificationUnseenCountLoadFulfilledAction => ({
  type: 'NOTIFICATION_UNSEEN_COUNT.LOAD_FULFILLED',
  payload: {
    unseenCount,
  },
});

const loadNotificationUnseenCountRejected = (error: unknown): NotificationUnseenCountLoadRejectedAction => ({
  type: 'NOTIFICATION_UNSEEN_COUNT.LOAD_REJECTED',
  payload: {
    error,
  },
  error: true,
});

const unseenNotificationCountRequest = (isRealtimeInitiated?: boolean) =>
  getGraphqlApiRequest({
    body: {
      operationName: 'NotificationUnseenCountClients',
      query: queryNotificationUnseenCount,
    },
    initiator: isRealtimeInitiated ? 'realtime' : undefined,
  });

type UnseenNotificationCountLifecycle = () => ManagedApiLifecycle<NotificationUnseenCountResponse>;
const unseenNotificationCountLifecycle: UnseenNotificationCountLifecycle = () => ({
  pending: () => loadNotificationUnseenCountPending(),
  fulfilled: (response) => loadNotificationUnseenCountFulfilled(response.data.viewer.notifications.viewerUnseenCount),
  rejected: (error) => loadNotificationUnseenCountRejected(error),
});

export interface LoadNotificationUnseenCountOptions {
  readonly isRealtimeInitiated?: boolean;
}
type LoadUnseenCount = (options?: LoadNotificationUnseenCountOptions) => PromiseThunkAction<void>;
export const loadUnseenCount: LoadUnseenCount =
  (options = { isRealtimeInitiated: false }) =>
  async (dispatch, getState) => {
    const state = getState();
    if (isPending(state, 'NotificationUnseenCountLoad', notificationUnseenCountRequestId)) {
      return;
    }

    const requestOptions = {
      request: unseenNotificationCountRequest(options.isRealtimeInitiated),
      lifecycle: unseenNotificationCountLifecycle(),
    };

    const action = options.isRealtimeInitiated
      ? retryableManagedApiAction(requestOptions, getRetryOptions(getState))
      : managedApiAction(requestOptions);

    try {
      await dispatch(action);
    } catch (error) {
      reportError({ error });
    }
  };

const getRetryOptions = (getState: () => YammerState): RetryOptions => ({
  shouldRetry: getShouldLoadUnseenCountWithSkipTelemetry(getState),
  retryDelay: getRetryDelay(),
  retryRequestFields: { initiator: 'realtime_retry' },
});

type ReportSkippedLoadUnseenCount = (reason: RequestSkippedReason) => void;
const reportSkippedLoadUnseenCount: ReportSkippedLoadUnseenCount = (reason) =>
  reportInfo({
    eventName: 'realtime_notifications_unseen_count_request_skipped',
    eventProperties: {
      reason,
    },
  });

type GetShouldLoadUnseenCountWithSkipTelemetry = (getState: () => YammerState) => () => boolean;
const getShouldLoadUnseenCountWithSkipTelemetry: GetShouldLoadUnseenCountWithSkipTelemetry = (getState) => {
  const requestTime = new Date().getTime();

  return () => {
    const state = getState();

    if (isPending(state, 'NotificationUnseenCountLoad', notificationUnseenCountRequestId)) {
      reportSkippedLoadUnseenCount('previousRequestPending');

      return false;
    }

    const fulfilledTime = getFulfilledTime(state, 'NotificationUnseenCountLoad', notificationUnseenCountRequestId);
    const hasLoadedSinceRequest = fulfilledTime && fulfilledTime > requestTime;
    if (hasLoadedSinceRequest) {
      reportSkippedLoadUnseenCount('newerRequestFulfilled');

      return false;
    }

    return true;
  };
};

type LoadMoreNotificationPending = (requestId: string) => NotificationLoadMorePendingAction;
const loadMoreNotificationPending: LoadMoreNotificationPending = (requestId) => ({
  type: 'NOTIFICATION.LOAD_MORE_PENDING',
  payload: {
    requestId,
  },
});

type LoadMoreNotificationFulfilled = (
  props: NotificationLoadMoreFulfilledAction['payload']
) => NotificationLoadMoreFulfilledAction;
const loadMoreNotificationFulfilled: LoadMoreNotificationFulfilled = ({
  requestId,
  unseenCount,
  entities,
  pageInfo,
}) => ({
  type: 'NOTIFICATION.LOAD_MORE_FULFILLED',
  payload: {
    requestId,
    unseenCount,
    entities,
    pageInfo,
  },
});

type LoadMoreNotificationRejected = (requestId: string, error: unknown) => NotificationLoadMoreRejectedAction;
const loadMoreNotificationRejected: LoadMoreNotificationRejected = (requestId, error) => ({
  type: 'NOTIFICATION.LOAD_MORE_REJECTED',
  payload: {
    requestId,
    error,
  },
  error: true,
});

type NotificationLoadMoreRequest = (
  beforeCursor: PageInfoFields['startCursor'],
  includeDelegateRequest: boolean
) => GraphqlApiRequest<NotificationLoadMoreClientsVariables>;
const notificationLoadMoreRequest: NotificationLoadMoreRequest = (beforeCursor, includeDelegateRequest) =>
  getGraphqlApiRequest({
    body: {
      operationName: 'NotificationLoadMoreClients',
      query: queryNotificationLoadMore,
      variables: {
        beforeCursor,
        notificationCount: notificationPageSize,
        includeDelegateRequest,
      },
    },
  });

type LoadMoreNotifications = () => PromiseThunkAction<void>;
export const loadMoreNotifications: LoadMoreNotifications = () => async (dispatch, getState) => {
  const state = getState();

  const requestCursor = state.notificationPaginations.startCursor;

  if (!requestCursor || isPending(state, 'NotificationLoadMore', requestCursor)) {
    return;
  }

  const includeDelegateRequest = getExperimentTreatment(state, 'DelegateRequestBell') === true;

  const request = notificationLoadMoreRequest(requestCursor, includeDelegateRequest);
  const lifecycle: ManagedApiLifecycle<NotificationLoadMoreResponse> = {
    pending: () => loadMoreNotificationPending(requestCursor),
    fulfilled: (response) => {
      const { entities, pageInfo } = normalizeNotificationsLoadMore(response);

      return loadMoreNotificationFulfilled({ entities, unseenCount: 0, pageInfo, requestId: requestCursor });
    },
    rejected: (error) => loadMoreNotificationRejected(requestCursor, error),
  };

  const action = managedApiAction({ request, lifecycle });

  try {
    await dispatch(action);
  } catch (error) {
    reportError({ error });
  }
};
